import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Divider } from '@mui/material';

const ImportReportDialog = ({ open, onClose, report }) => {
  if (!report) return null;

  const totals = report.totals || {};
  const workbook = report.workbook_stats || {};
  const sheets = report.sheets || [];

  const downloadBlob = (content, filename, type = 'text/plain') => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const buildCsv = (rows) => rows.map(r => r.map(v => {
    const s = String(v ?? '');
    if (s.includes(',') || s.includes('\n') || s.includes('"')) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  }).join(',')).join('\n');

  const handleDownloadBlocksCsv = () => {
    const rows = [[
      'sheet', 'block_index', 'header_row', 'columns_mapped_count', 'rows_candidates', 'skipped_empty', 'rejected', 'rejection_reasons'
    ]];
    sheets.forEach(s => {
      (s.blocks || []).forEach(b => {
        rows.push([
          s.name,
          b.block_index,
          b.header_row,
          (b.columns_mapped || []).length,
          b.rows_candidates,
          b.skipped_empty,
          b.rejected,
          (b.rejection_reasons || []).join('; ')
        ]);
      });
    });
    downloadBlob(buildCsv(rows), 'import_blocks.csv', 'text/csv');
  };

  const handleDownloadIssuesCsv = () => {
    const rows = [['sheet', 'block_index', 'row_index', 'issue', 'title']];
    sheets.forEach(s => {
      (s.blocks || []).forEach(b => {
        (b.candidates || []).forEach(c => {
          (c.issues || ['ok']).forEach(issue => {
            rows.push([
              c.provenance?.sheet_name || s.name,
              c.provenance?.block_index ?? b.block_index,
              c.provenance?.row_index ?? '',
              issue,
              c.fields?.title || '(untitled)'
            ]);
          });
        });
      });
    });
    downloadBlob(buildCsv(rows), 'import_issues.csv', 'text/csv');
  };

  const handleDownloadMissedCsv = () => {
    // Missed = rejected blocks' potential rows (we only have counts). Provide a summary instead.
    const rows = [['sheet', 'block_index', 'reason', 'rows_candidates']];
    sheets.forEach(s => {
      (s.blocks || []).forEach(b => {
        if (b.rejected) {
          rows.push([s.name, b.block_index, (b.rejection_reasons || []).join('; '), b.rows_candidates]);
        }
      });
    });
    downloadBlob(buildCsv(rows), 'import_missed_blocks.csv', 'text/csv');
  };

  const handleDownloadRawJson = () => {
    downloadBlob(JSON.stringify(report, null, 2), 'import_raw.json', 'application/json');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Import Report</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>Summary</Typography>
          <Typography variant="body2" color="text.secondary">
            Sheets: {workbook.sheets_total} (test-case: {workbook.sheets_test_case}, non-test-case: {workbook.sheets_non_test_case})
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Candidates: {totals.candidates} • Will Generate: {totals.will_generate} {report.totals?.created != null && `• Created: ${report.totals.created}`}
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>Per Sheet</Typography>
          {sheets.map((s, i) => (
            <Box key={i} sx={{ mb: 1.5, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                Blocks: {(s.blocks || []).length} • Candidates: {s.totals?.candidates} • Skipped: {s.totals?.skipped} {s.reasons && s.reasons.length > 0 ? `• Reasons: ${s.reasons.join(', ')}` : ''}
              </Typography>
        {s.suspect_headers && s.suspect_headers.length > 0 && (
          <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
            ⚠️ {s.suspect_headers.length} suspect header(s): rows {s.suspect_headers.slice(0, 5).map(h => h.row_index).join(', ')}
          </Typography>
        )}
        {s.assumptions && s.assumptions.using_assumptions && (
          <Box sx={{ mt: 1, p: 1, bgcolor: 'warning.light', borderRadius: 1 }}>
            <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 'bold', display: 'block' }}>
              ⚠️ Assumptions Made
            </Typography>
            <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mt: 0.5 }}>
              {s.assumptions.assumption_reason}
            </Typography>
            <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mt: 0.5 }}>
              Assumed headers: {s.assumptions.assumed_headers?.join(', ')}
            </Typography>
            <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
              Please verify the field mapping is correct. If not, add proper headers to this sheet.
            </Typography>
          </Box>
        )}
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDownloadBlocksCsv}>Download Blocks CSV</Button>
        <Button onClick={handleDownloadIssuesCsv}>Issues CSV</Button>
        <Button onClick={handleDownloadMissedCsv}>Missed Blocks CSV</Button>
        <Button onClick={handleDownloadRawJson} color="secondary">Raw JSON</Button>
        <Button onClick={onClose} variant="contained">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportReportDialog;


